<template>
  <div class="material">
    <div v-if="!selectedMaterials" class="no material-card">
      <span>Please select a material</span>
    </div>
    <div v-if="!!selectedMaterials" class="material-info-container">
      <object-main-info
        :fields="fields"
        :identifier="material ? material.sm_reference : ''"
        :identifier-label="$tc('Code', 1) + ' / ' + $tc('Reference', 1)"
        :editing-data="editingData"
        :show-edit-button="actionToExecute !== 'CREATE_NEW_MATERIAL'"
        :card-label="cardLabel"
        :height="editingData ? '40%' : '30%'"
        @toggle-edit="toggleEdit"
        @update-object="updateMaterial"
      />
      <material-specific-info
        :materialFamilyID="materialFamilyID"
        :materialSpecificFields="material ? material.family_specific_fields : familySpecificFields"
        :accessories="accessoriesSelected"
        :others="othersSelected"
        :editing-data="editingData"
        :lossValue="material ? material.sm_loss_value : null"
        :installationTypeOptions="installationTypeOptions"
        :required-fields-filled="requiredFieldsAreFilled"
        :submit-button-label="submitButtonLabel"
        :custom-fields-inputs="customFieldsInputs"
        :custom-fields-values="material ? material.custom_fields : {}"
        @update-material="updateMaterialSpecificField"
        @update-custom-field="updateMaterialCustomField"
        @update-loss-value="updateMaterial"
        @update-required-field="specificRequiredFieldsAreFilled = $event"
        @cancel-edit="discardChangesDialogIsOpen = true"
        @save-material="saveChangesDialogIsOpen = true"
      />
    </div>
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="var(--app-primary-base)"
      style="position: absolute;"
    />
    <dialog-choice
      :isOpen="saveChangesDialogIsOpen"
      :title="$t('SaveChanges')"
      :content="$t('SaveChangesContent')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="saveMaterial"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="saveChangesDialogIsOpen = false"
      icon="mdi-content-save"
      iconColor="var(--app-primary-base)"
    />
    <dialog-choice
      :isOpen="discardChangesDialogIsOpen"
      :title="$t('DiscardChanges')"
      :content="$t('ClusterCreationConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="cancelEdit"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="discardChangesDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="var(--app-primary-base)"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import MaterialMainInfo from './MaterialMainInfo';
import ObjectMainInfo from '@/newComponents/_Organisms/ObjectMainInfo';
import MaterialSpecificInfo from '@/newComponents/StockManagement/MaterialSpecificInfo.vue';
import _ from 'lodash';
import utils from "@/utils";
import LoadingSpinner from "vue-loading-overlay";
import { materialFamilyMixin } from "@/mixins/management/materialFamilyMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import DialogChoice from '../_Molecules/DialogChoice.vue';
import { bus } from "@/main.js";
import { unitConverterMixin } from "@/mixins/management/unitConverterMixin";

export default {
  name: "Material",

  components: {
    MaterialMainInfo,
    MaterialSpecificInfo,
    LoadingSpinner,
    DialogChoice,
    ObjectMainInfo,
  },

  mixins: [materialFamilyMixin, objectManagementMixin,unitConverterMixin],

  props: {
    selectedMaterials: {
      type: Object,
    },

    activeTabMaterialFamily: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      material: null,
      accessoriesSelected: [],
      othersSelected: [],
      customFieldsInputs: [],
      editingData: false,
      originalMaterial: {},
      installationTypeOptions: [],
      specificRequiredFieldsAreFilled: false,
      dataToSend: {},
      validation: {
        badUnitPrice: false,
        badLaborPrice: false,
      },
      unitFields:[
        { name: "smc_max_cable_length", unit:"m" },
        { name: "smcl_cable_stub_length", unit:"m" },
        { name: "vertical_drop_max_cable_length", unit:"m" },
        { name: "smc_diameter", unit:"m" },
        { name: "smcl_length", unit:"m" },
        { name: "smcl_width", unit:"m" },
        { name: "smcl_height", unit:"m" },
        { name: "smch_length", unit:"m" },
        { name: "smch_width", unit:"m" },
        { name: "smch_depth", unit:"m" },
        { name: "smcb_length", unit:"m" },
        { name: "smcb_width", unit:"m" },
        { name: "smcb_depth", unit:"m" },
        { name: "smtb_length", unit:"m" },
        { name: "smtb_width", unit:"m" },
        { name: "smtb_depth", unit:"m" },
        { name: "smts_length", unit:"m" },
        { name: "smts_width", unit:"m" },
        { name: "smts_height", unit:"m" },
        { name: "smrc_height", unit:"m" },
        { name: "smrc_width", unit:"m" },
        { name: "smrc_depth", unit:"m" },
      ],
      actionToExecute: 'UPDATE_SELECTED_MATERIAL',
      submitButtonLabel: 'Save',
      cardLabel: 'EditMaterial',
      saveChangesDialogIsOpen: false,
      discardChangesDialogIsOpen: false,
      networkRulesOptions: [],
    }
  },

  watch: {
    selectedMaterials(value) {
      this.loadInstallationTypeOptions();
      this.loadNetworkRulesOptions();
      if (value !== null && Object.keys(value).length === 0) { //if material is an empty object for creation
        this.setCreateMaterialVariables();
      } else if (!!value) { // if the material is a normal object to be edited
        this.setEditMaterialVariables();
      } else if (value === null) { // if there is no object
        this.clearVariables();
      }
    },

    editingData() {
      bus.$emit("toggle-create-edit-flag")
    },

    "material.sm_labor_price"(value) {
      this.validation.badLaborPrice = value < 0;
    },

    "material.sm_unit_price"(value) {
      this.validation.badUnitPrice = value < 0;
    },

    "material.sm_family_id"(){
       if(this.actionToExecute === 'CREATE_NEW_MATERIAL'){
        this.material.family_specific_fields = this.setMaterialFamilySpecificFields();
      }
      if (this.material.sm_family_id) {
        this.getCustomFieldsByMaterialId()
      }
    },
  },

  computed: {
    materialFamilyID() {
      return this.material ? this.material.sm_family_id : this.activeTabMaterialFamily;
    },

    requiredFieldsAreFilled() {
      return (this.material.sm_family_id === "cd" ? true : this.specificRequiredFieldsAreFilled) &&
        !!this.material.sm_family_id &&
        !!this.material.sm_reference &&
        !!this.material.sm_description &&
        !this.validation.badLaborPrice &&
        !this.validation.badUnitPrice
    },

    creatingMaterial() {
      return this.actionToExecute === 'CREATE_NEW_MATERIAL';
    },

    familyOptions() {
      return this.$store.getters.optionLists_materialFamily;
    },

    fields() {
      return this.material !== null ? [
        {
          icon: "label",
          label: this.$i18n.t("MaterialFamily"),
          value: !this.editingData
            ? this.parseSelectValue([this.material.sm_family_id], this.familyOptions)
            : this.material.sm_family_id,
          varName: 'sm_family_id',
          inputType: 'select',
          disabled: this.creatingMaterial ? false : true,
          required: true,
          options: this.familyOptions,
          clearable: false,
        },
        {
          icon: "" ,
          label: this.$i18n.t("MaterialReference"),
          value: this.material.sm_reference,
          varName: 'sm_reference',
          disabled: false,
          required: true,
          inputType: 'text',
          size: (100/3)*2+'%',
        },
        {
          icon: "label",
          label: this.$i18n.t("ShortDescription"),
          value: this.material.sm_short_description,
          disabled: false,
          inputType: 'text',
          varName: 'sm_short_description',
        },
        {
          icon: "description",
          label: this.$i18n.t("Description"),
          value: this.material.sm_description,
          size: (100/3)*2+'%',
          disabled: false,
          required: true,
          inputType: 'text',
          varName: 'sm_description',
        },
        {
          icon: "business",
          label: this.$i18n.tc("Vendor"),
          value: this.material.sm_vendor,
          disabled: false,
          inputType: 'text',
          varName: 'sm_vendor',
        },
        {
          icon: "precision_manufacturing",
          label: this.$i18n.tc("Manufacturer"),
          value: this.material.sm_manufacturer,
          disabled: false,
          inputType: 'text',
          varName: 'sm_manufacturer',
        },
        {
          icon: "vertical_split",
          label: this.$i18n.tc("Barcode"),
          value: this.material.sm_barcode,
          disabled: true,
          inputType: 'int',
          varName: 'sm_barcode',
        },
        {
          icon: "local_offer",
          label: this.$i18n.tc("LaborPrice"),
          value: this.material.sm_labor_price
            ? this.material.sm_labor_price
            : "-",
          disabled: false,
          inputType: 'float',
          varName: 'sm_labor_price',
          min: 0,
          step: 0.01
        },
        {
          icon: "payment",
          label: this.$i18n.tc("PriceUnit"),
          value: this.material.sm_unit_price ? this.material.sm_unit_price : "-",
          disabled: false,
          inputType: 'float',
          varName: 'sm_unit_price',
          min: 0,
          step: 0.01
        },
        {
          icon: "info_outline",
          label: this.$i18n.tc("UnitaryValue"),
          value: this.material.sm_unitary_value ? 'True' : 'False',
          disabled: false,
          inputType: 'radio',
          radioOptions: [{ value: 'True', label: this.$i18n.t('Yes') }, { value: 'False', label: this.$i18n.t('No') }],
          varName: 'sm_unitary_value',
        },
        {
          icon: "info_outline",
          label: this.$i18n.tc("NetworkRules"),
          value: !this.editingData
            ? this.parseSelectValue(this.material.network_rules, this.networkRulesOptions)
            : this.material.network_rules,
          varName: 'network_rules',
          inputType: 'multiselect',
          disabled: false,
          options: this.networkRulesOptions,
          clearable: false,
          size: this.editingData ? '33%' : '100%'
        },
      ] : [];
    },
  },

  methods: {
    ...mapActions([
      "GET_STOCK_MATERIAL",
      "fetchCustomFields",
      "GET_LIST",
      "UPDATE_SELECTED_MATERIAL",
      "FETCH_ALL_NETWORK_RULES_TEMPLATES",
    ]),

    async getMaterialByUUID() {
      let uuid = this.selectedMaterials.sm_uuid;
      this.accessoriesSelected = [];
      this.othersSelected = [];
      this.loading = true;
      try {
        const material = await this.GET_STOCK_MATERIAL({
          uuid: uuid,
          append: "family_specific_fields,network_rules,others"
        });

        this.material = material;
        if (localStorage.getItem("measureSystem") === 'imperial') {
          this.unitFields.forEach(field => {
            if (material.family_specific_fields[field.name]) {
              this.material.family_specific_fields[field.name] = this.convertFromMetricToImperial(field.unit, material.family_specific_fields[field.name]).toFixed(field.precision ? field.precision : 2);
            }
          });
        }

        if (this.material.sm_family_id === "ac" || this.material.sm_family_id === "ot") {
          this.formatAccessoriesOthersSelectedField(this.material.family_specific_fields);
        }

        this.fillAccessoriesAndOthersArrays();

        if (!this.material.custom_fields) {
          this.material.custom_fields = {};
        }
        if (this.material.sm_family_id) {
          this.getCustomFieldsByMaterialId();
        }
        this.originalMaterial = _.cloneDeep(this.material);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    formatAccessoriesOthersSelectedField(fields) {
      this.material.family_specific_fields = {
        stock_material_other: []
      }
      if (fields && fields.length > 0) {
        fields.forEach(field => {
          if (field && field.code) {
            this.material.family_specific_fields.stock_material_other.push(field.code);
          }
        });
      }
    },

    /**
     * Returns the custom fields of the selected material id
     * @returns {Promise<void>}
     */
    async getCustomFieldsByMaterialId() {
      try {
        let customFieldsInputs = await this.fetchCustomFields({
          'filter[object_type]': this.materialFamilyID
        });
        this.customFieldsInputs = customFieldsInputs.data
      } catch (e) {
        console.error(e);
        this.$toasted.global.toast_error(e);
      }
    },

    getListItemName(list, itemID) {
      let item = list.find(material => material.code === itemID);
      if (item !== undefined) {
        if (item.hasOwnProperty("name")) {
          return item.name;
        } else {
          return this.getListElementLabel(item);
        }
      }
      return "";
    },

    updateMaterial(updatedValue) {
      this.material[updatedValue.varName] = updatedValue.value;
    },

    updateMaterialSpecificField(updatedValue) {
      this.material.family_specific_fields[updatedValue.varName] = updatedValue.value;
    },

    updateMaterialCustomField(updatedValue) {
      this.material.custom_fields[updatedValue.varName] = updatedValue.value;
      bus.$emit('update-custom-field-value');
    },

    async loadInstallationTypeOptions() {
      try {
        this.installationTypeOptions = await this.GET_LIST({
          listName: "list_installation_type",
          code:
            this.material &&
            this.material.family_specific_fields &&
            this.material.family_specific_fields.installation_type
              ? this.material.family_specific_fields.installation_type
              : []
        });
      } catch (error) {
        console.error(error);
        this.installationTypeOptions = [];
      }
    },

    cancelEdit() {
      this.discardChangesDialogIsOpen = false;
      this.editingData = false;
      if (this.material.sm_uuid) {
        this.material = _.cloneDeep(this.originalMaterial);
      } else {
        this.$emit('clear-selected-item');
      }
      this.fillAccessoriesAndOthersArrays();
    },

    toggleEdit() {
      if (this.editingData) {
        this.discardChangesDialogIsOpen = true;
      } else {
        this.editingData = !this.editingData
      }
    },

    fillAccessoriesAndOthersArrays() {
      this.accessoriesSelected = [];
      this.othersSelected = [];
      if (this.material.others) {
        for (let item of this.material.others) {
          if (item.sm_family_id === "ac") {
            this.accessoriesSelected.push(item);
          } else {
            this.othersSelected.push(item);
          }
        }
      }
    },

    async saveMaterial() {
      this.saveChangesDialogIsOpen = false;
      try {
        this.loading = true;

        this.createDataToSend();

        this.dataToSend.other = [];
        if (
          this.material.sm_family_id !== "ac" &&
          this.material.sm_family_id !== "ot" &&
          (this.accessoriesSelected.length > 0 || this.othersSelected.length > 0)
        ) {
          this.generateAccessoriesDataSend(this.accessoriesSelected);
          this.generateAccessoriesDataSend(this.othersSelected);
        }

        if (Object.keys(this.dataToSend).length > 0) {
          if (!this.creatingMaterial) {
            this.dataToSend.uuid = this.material.sm_uuid;
          }
          await this.$store.dispatch(this.actionToExecute, [this.dataToSend]);
        }

        this.editingData = false;

        if (!this.creatingMaterial) {
          this.getMaterialByUUID();
          this.$toasted.global.toast_success(this.$i18n.t('MaterialUpdatedSuccessfully'));
        } else {
          this.$emit('clear-selected-item');
          this.$toasted.global.toast_success(this.$i18n.t('MaterialCreatedSuccessfully'));
        }

        bus.$emit('reload-materials');
      } catch (error) {
        console.error(error);
        this.handleError(error);
      } finally {
        this.loading = false;
        this.dataToSend = {};
      }
    },

    /** Fills the object to send to the server by putting a watcher in each variable of the object */
    createDataToSend() {
      Object.keys(this.material).forEach(key => {
        if (key.includes("sm_") && this.material[key] !== this.originalMaterial[key]) {
          let newKey = key.replace("sm_", "");
          this.dataToSend[newKey] = this.material[key];
        }
      });

      if (this.material.family_specific_fields) {
        Object.keys(this.material.family_specific_fields).forEach(key => {
          if (this.valuesAreDifferent(this.material.family_specific_fields[key], this.originalMaterial.family_specific_fields[key])) {
            let newKey;
            if (key.startsWith("sm")) {
              newKey = key.substring(key.indexOf("_") + 1);
            } else {
              newKey = key;
            }

            if (!this.dataToSend.hasOwnProperty("family_specific_fields")) {
              this.dataToSend.family_specific_fields = {};
            }
            let unitField = this.unitFields.find(field => field.name === key)
            if(unitField && localStorage.getItem("measureSystem") === 'imperial'){
              this.dataToSend.family_specific_fields[newKey] = this.convertFromImperialToMetric(unitField.unit, this.material.family_specific_fields[key]).toFixed(unitField.precision ? unitField.precision : 2);
            } else {
              this.dataToSend.family_specific_fields[newKey] = this.material.family_specific_fields[key];
            }
          }
        });
      }

      if (this.material.custom_fields) {
        Object.keys(this.material.custom_fields).forEach(key => {
          if (this.valuesAreDifferent(this.material.custom_fields[key], this.originalMaterial.custom_fields[key])) {

            if (!this.dataToSend.hasOwnProperty("custom_fields")) {
              this.dataToSend.custom_fields = {};
            }

            this.dataToSend.custom_fields[key] = this.material.custom_fields[key];
          }
        });
      }

      if (this.valuesAreDifferent(this.material.network_rules, this.originalMaterial.network_rules)) {
        this.dataToSend.network_rules = this.material.network_rules;
      }
    },

    valuesAreDifferent(currentValue, originalValue) {
      if (typeof(currentValue) === typeof(originalValue)) {
        if (Array.isArray(currentValue)) {
          return !_.isEmpty(_.xor(currentValue, originalValue))
        } else {
          return currentValue !== originalValue;
        }
      } else {
        return true;
      }
    },

    generateAccessoriesDataSend(list) {
      if (list) {
        for (let accessory of list) {
          this.dataToSend.other.push({
            id: accessory.sm_id,
            quantity: accessory.sm_quantity
          });
        }
      }
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);

      if (typeof errorHandle === "object") {
        if (errorHandle.hasOwnProperty("material.sm_reference")) {
          this.$toasted.global.toast_success(this.$i18n.t('ValidReference'));
        }
        if (errorHandle.hasOwnProperty("material.sm_family_id")) {
          this.$toasted.global.toast_success(this.$i18n.t('ValidFamilyID'));
        }
        if (errorHandle.hasOwnProperty("material.sm_description")) {
          this.$toasted.global.toast_success(this.$i18n.t('ValidDescription'));
        }
      } else if (typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      }
    },

    setCreateMaterialVariables() {
      this.material = {
        sm_family_id: this.activeTabMaterialFamily,
        sm_reference: "",
        sm_short_description: "",
        sm_description: "",
        sm_barcode: undefined,
        sm_manufacturer: undefined,
        sm_vendor: undefined,
        sm_unit_price: undefined,
        sm_unitary_value: false,
        sm_labor_price: undefined,
        sm_loss_value: "0.00001",
        network_rules: [],
        other: [],
        family_specific_fields: {},
        custom_fields: {}
      };
      this.originalMaterial = {
        network_rules: [],
        other: [],
        family_specific_fields: {},
        custom_fields: {}
      };
      this.accessoriesSelected = [];
      this.othersSelected = [];
      this.material.family_specific_fields = this.setMaterialFamilySpecificFields();
      this.getCustomFieldsByMaterialId();
      this.actionToExecute = 'CREATE_NEW_MATERIAL';
      this.submitButtonLabel = 'Create';
      this.cardLabel = 'AddMaterial';
      this.editingData = true;
    },

    setMaterialFamilySpecificFields() {
      switch (this.material.sm_family_id) {
        case "ud":
          return {
            smud_microduct_core: false,
          }
        default:
          return {}
      }
    },

    setEditMaterialVariables() {
      this.getMaterialByUUID();
      this.actionToExecute = 'UPDATE_SELECTED_MATERIAL';
      this.submitButtonLabel = 'Save';
      this.cardLabel = 'EditMaterial';
    },

    clearVariables() {
      this.material = null;
      this.originalMaterial = null;
      this.editingData = false;
    },

    async loadNetworkRulesOptions() {
      try {
        let templates = await this.FETCH_ALL_NETWORK_RULES_TEMPLATES({
          type: "type",
          value: "Calculation",
          queryParams: {
            paginate: false
          },
        });
        this.networkRulesOptions = [];
        templates.data.forEach(template => {
          this.networkRulesOptions.push({
            code: template.id,
            libelle: template.name
          });
        });
      } catch (error) {
        console.error(error);
      }
    },
  },
}
</script>

<style scoped>
.material {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
}

.material-card {
  height: 100%;
}

.material-card {
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.material-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
}

.no.material-card > span {
  color: var(--app-primary-base);
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.material-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/StockManagement/Material.vue